import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NavBar from '../../components/common/NavBar';
import TimeSeriesChart from '../../components/common/TimeSeriesChart';
import CsvDownloadButton from '../../components/common/CsvDownloadButton';
import AboutThisData from '../../components/data/AboutThisData';
import DataStateNotice from '../../components/data/DataStateNotice';
import ResultCapNotice from '../../components/data/ResultCapNotice';
import SourceBadge from '../../components/data/SourceBadge';
import DraftDataBadge from '../../components/data/DraftDataBadge';
import ExportAllRowsButton from '../../components/data/ExportAllRowsButton';
import DistributionPreview from './DistributionPreview';
import type { DistributionPreviewState } from './DistributionPreview';
import type { ActivityRow } from '../../services/activitiesApi';
import type { ActivitiesRequest } from '../../services/activityDistributionsApi';
import { isDemoJobId } from '../../services/demoApi';
import { COUNTRIES, normalizeCountry } from '../../config/countries';
import type { CountryCode } from '../../config/countries';
import { GRANULARITY_LABELS, MEASURES, isActivityMeasure, offeredActivityMeasures, pendingFor } from '../../config/products';
import type { ActivityMeasure, MeasureKey } from '../../config/products';
import { buildChartSeries } from '../../utils/chartSeries';
import { csvFilename } from '../../utils/csv';
import type { Cell } from '../../utils/csv';
import { formatDayLabel } from '../../utils/dateWindow';
import { pluralize } from '../../utils/plural';
import { CONTEXT_HEADERS, contextValues } from '../../utils/exportContext';
import { activityBackState } from '../../utils/returnState';
import type { WindowUtc } from '../../utils/submittedWindow';

interface ActivityPreviewState {
  jobId: string;
  jobResults: ActivityRow[];
  jobStatus?: any;
  geometries?: any[];
  selectedGrids?: any[];
  geoType?: string;
  countryCode?: string;
  /** The delivery day and resolution the job was run for, handed back on the way to the map page. */
  day?: string;
  granularity?: 'hourly' | 'daily';
  measure?: string;
  /** The start and end instants the job was submitted with, handed back with the rest. */
  windowUtc?: WindowUtc | null;
  /** The picker's query string, handed back with the rest. */
  pickerSearch?: string | null;
  /** The API cut the preview: `jobResults` are not all the rows. */
  truncated?: boolean;
  /** What was submitted, for Export all rows and the way back. */
  request?: ActivitiesRequest | null;
}

/**
 * The measure the figures are: the one the map page handed over, else the first the registry offers
 * for the country and area type.
 */
function measureOf(state: ActivityPreviewState, country: CountryCode | null): ActivityMeasure | null {
  if (typeof state.measure === 'string' && isActivityMeasure(state.measure as MeasureKey)) {
    return state.measure as ActivityMeasure;
  }
  return offeredActivityMeasures(country, state.geoType ?? null)[0] ?? null;
}

const TotalsPreview: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortField, setSortField] = useState<keyof ActivityRow>('geo_id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);

  const state = location.state as ActivityPreviewState | null;

  // One line per area. A row without a count stays in as a gap, not a zero.
  const series = useMemo(
    () =>
      buildChartSeries(state?.jobResults ?? [], {
        key: (r) => r.geo_id,
        label: (r) => r.geo_name ?? `Area ${r.geo_id}`,
        time: (r) => r.time_bucket,
        value: (r) => r.value,
      }),
    [state?.jobResults],
  );

  // Check if we have multiple time periods
  const hasMultipleTimePeriods = useMemo(() => {
    if (!state?.jobResults) return false;
    const uniqueTimePeriods = new Set(state.jobResults.map(r => r.time_bucket));
    return uniqueTimePeriods.size > 1;
  }, [state?.jobResults]);

  // Auto-collapse table if it has many rows
  useEffect(() => {
    if (state?.jobResults) {
      setIsTableCollapsed(state.jobResults.length > 100);
    }
  }, [state?.jobResults]);

  useEffect(() => {
    if (!state?.jobResults) {
      // Redirect back if no data
      navigate('/activities');
    }
  }, [state, navigate]);

  if (!state?.jobResults) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">No Preview Data</h2>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/activities')}
          >
            Return to Activities
          </button>
        </div>
      </div>
    );
  }

  const { jobId, jobResults, day, granularity } = state;
  const country = normalizeCountry(state.countryCode);
  const measure = measureOf(state, country);
  const measureLabel = measure ? MEASURES[measure].label : 'Value';

  // Where Back leads: the map page, with the job, its areas and the window it was run for. The
  // map page writes the same state into its own history entry before opening this page, so the
  // browser's Back button restores exactly what the Back button here does.
  const backState = activityBackState({ ...state, measure, windowUtc: state.windowUtc ?? null });
  const { request } = state;

  // Sorting logic. A time bucket is compared as the text it shows.
  const sortValue = (r: ActivityRow) => r[sortField];
  const sortedResults = [...jobResults].sort((a, b) => {
    const aValue = sortValue(a);
    const bValue = sortValue(b);

    // A missing value (no count for this row) sorts after every number, whatever the direction.
    if (aValue == null || bValue == null) {
      if (aValue == null && bValue == null) return 0;
      return aValue == null ? 1 : -1;
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    const aStr = String(aValue);
    const bStr = String(bValue);
    // Area ids are text now; compare their digits as numbers so 1000 comes after 999.
    return sortDirection === 'asc'
      ? aStr.localeCompare(bStr, undefined, { numeric: true })
      : bStr.localeCompare(aStr, undefined, { numeric: true });
  });

  // Pagination logic
  const totalPages = Math.ceil(sortedResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResults = sortedResults.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field: keyof ActivityRow) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (field: keyof ActivityRow) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Unique people are not additive across areas or hours, so there is no total or average here:
  // only figures that mean something on their own.
  const counts = jobResults.map((r) => r.value).filter((v): v is number => v != null);
  const maxPeople = Math.max(0, ...counts);
  const areaCount = new Set(jobResults.map(r => r.geo_id)).size;
  const bucketCount = new Set(jobResults.map(r => r.time_bucket)).size;

  // The file states what it holds: country, source, measure and the UTC window of the job.
  const context: string[] =
    country && measure
      ? contextValues(country, 'activities', measure, state.windowUtc ?? null)
      : CONTEXT_HEADERS.map(() => '');
  const csvRows: Cell[][] = [
    ['Area ID', 'Area name', measureLabel, 'Time bucket (UTC)', 'Type', ...CONTEXT_HEADERS],
    ...jobResults.map((r): Cell[] => [r.geo_id, r.geo_name, r.value, r.time_bucket, r.type, ...context]),
  ];
  const csvName = csvFilename('activities', [country ? COUNTRIES[country].label : '', day, granularity].filter(Boolean).join(' '));

  return (
    <div className="min-h-screen bg-base-200">
      <NavBar title="Activity Results Preview" showBackButton backTo="/activities-map" backState={backState} />

      <div className="container mx-auto px-4 py-6">
        {/* Header Section */}
        <div className="bg-surface rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-ink">Activity Results Preview</h1>
                {country ? <SourceBadge country={country} product="activities" /> : null}
                <DraftDataBadge country={country} product="activities" geoType={state.geoType} />
              </div>
              <p className="text-ink-muted mt-1">Job ID: {jobId}</p>
              {(day || granularity) && (
                <p className="text-ink-muted mt-1">
                  {day ? formatDayLabel(day) : ''}{day && granularity ? ' · ' : ''}{granularity ? GRANULARITY_LABELS[granularity] : ''} · UTC
                </p>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm text-ink-subtle">Total Results</div>
              <div className="text-2xl font-bold text-note-ink">{jobResults.length}</div>
            </div>
          </div>

          {country ? (
            <div className="mb-4">
              <AboutThisData country={country} product="activities" measure={measure ?? undefined} />
            </div>
          ) : null}

          {/* Summary Stats */}
          {jobResults.length === 0 ? (
            <DataStateNotice
              tone="info"
              title="No rows for this selection"
              body={["Cells or municipalities with fewer than 5 people are not published.", pendingFor(country, 'activities', state.geoType)?.reason].filter(Boolean).join(' ')}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div role="group" aria-label="Peak value" className="bg-paper-100 text-ink rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{counts.length > 0 ? maxPeople.toLocaleString() : '–'}</div>
                <div className="text-sm text-ink-muted">Peak value</div>
              </div>
              <div role="group" aria-label="Areas" className="bg-paper-100 text-ink rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{areaCount.toLocaleString()}</div>
                <div className="text-sm text-ink-muted">Areas</div>
              </div>
              <div role="group" aria-label="Time buckets" className="bg-paper-100 text-ink rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{bucketCount.toLocaleString()}</div>
                <div className="text-sm text-ink-muted">Time buckets</div>
              </div>
            </div>
          )}
          <div className="mt-4 empty:hidden">
            <ResultCapNotice rows={jobResults} truncated={state.truncated} />
          </div>
        </div>

        {/* Time Series Chart */}
        {hasMultipleTimePeriods && (
          <div className="bg-surface rounded-lg shadow p-6 mb-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-ink flex items-center gap-2">
                📈 Time Series Analysis
              </h2>
              <p className="text-sm text-ink-muted mt-1">
                {measureLabel} across the selected time periods
              </p>
            </div>
            <div className="h-64">
              <TimeSeriesChart
                series={series}
                title={`${measureLabel} Over Time`}
                yLabel={measureLabel}
                seriesNoun="areas"
              />
            </div>
          </div>
        )}

        {/* Table Controls: with no rows there is nothing to page through or hide */}
        {jobResults.length > 0 && (
        <div className="bg-surface rounded-lg shadow p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                className="btn btn-sm btn-outline"
                onClick={() => setIsTableCollapsed(!isTableCollapsed)}
              >
                {isTableCollapsed ? '📊 Show Table' : '📊 Hide Table'}
              </button>
              {!isTableCollapsed && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-ink-muted">Show:</span>
                  <select
                    className="select select-sm select-bordered"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-ink-muted">results per page</span>
                </div>
              )}
            </div>

            <div className="text-sm text-ink-muted">
              {isTableCollapsed ? (
                `${pluralize(jobResults.length, 'result')} (table hidden)`
              ) : (
                `Showing ${startIndex + 1}-${Math.min(startIndex + itemsPerPage, jobResults.length)} of ${pluralize(jobResults.length, 'result')}`
              )}
            </div>
          </div>
        </div>
        )}

        {/* Results Table */}
        {jobResults.length > 0 && !isTableCollapsed && (
        <div className="bg-surface rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr>
                  <th
                    className="cursor-pointer hover:bg-paper-100"
                    onClick={() => handleSort('geo_id')}
                  >
                    Area ID {getSortIcon('geo_id')}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-paper-100"
                    onClick={() => handleSort('geo_name')}
                  >
                    Area name {getSortIcon('geo_name')}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-paper-100"
                    onClick={() => handleSort('value')}
                  >
                    {measureLabel} {getSortIcon('value')}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-paper-100"
                    onClick={() => handleSort('time_bucket')}
                  >
                    Time bucket (UTC) {getSortIcon('time_bucket')}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-paper-100"
                    onClick={() => handleSort('type')}
                  >
                    Type {getSortIcon('type')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedResults.map((result, index) => (
                  <tr key={`${result.geo_id}-${index}`}>
                    <td className="font-mono text-sm">{result.geo_id}</td>
                    <td>{result.geo_name ?? '-'}</td>
                    <td>
                      {(() => {
                        const people = result.value;
                        if (people == null) return <span className="text-ink-muted">–</span>;
                        const share = maxPeople > 0 ? (people / maxPeople) * 100 : 0;
                        return (
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold text-note-ink">
                              {people.toLocaleString()}
                            </span>
                            <div
                              className="w-16 h-2 bg-paper-300 rounded-full overflow-hidden"
                              title={`${share.toFixed(1)}% of max`}
                            >
                              <div
                                className="h-full bg-series-2 rounded-full"
                                style={{ width: `${share}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="text-sm">{result.time_bucket}</td>
                    <td>
                      <span className="badge badge-outline badge-sm">{result.type}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <button
                className="btn btn-sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              <div className="flex items-center space-x-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + Math.max(1, currentPage - 2);
                  if (pageNum > totalPages) return null;

                  return (
                    <button
                      key={pageNum}
                      className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span className="text-ink-subtle">...</span>
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>

              <button
                className="btn btn-sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center items-start mt-6 gap-4">
          <button
            className="btn btn-outline"
            onClick={() => navigate('/activities-map', { state: backState })}
          >
            ← Back to Results
          </button>
          <CsvDownloadButton rows={csvRows} filename={csvName} disabled={jobResults.length === 0} />
          {request && !isDemoJobId(jobId) ? <ExportAllRowsButton request={request} filename={csvName.replace(/^activities-/, 'activities-all-rows-')} /> : null}
        </div>
      </div>
    </div>
  );
};

// A distribution job has a preview of its own; everything else is the job's one measure.
const ActivityPreview: React.FC = () => {
  const location = useLocation();
  const state = location.state as ActivityPreviewState | null;
  return state?.request?.kind === 'distributions' ? (
    <DistributionPreview state={state as unknown as DistributionPreviewState} />
  ) : (
    <TotalsPreview />
  );
};

export default ActivityPreview;